import { ArgumentException } from '../../../exceptions/argument-exception';
import { Currency } from '../../../models/serializable/currency';
import { GenericOrder } from '../../../models/serializable/orders/generic-order';
import { GenericOrderProductEntry } from '../../../models/serializable/orders/generic-order-entries/generic-order-product-entry';
import { GenericOrderEntry, GenericOrderEntryStatus } from '../../../models/serializable/orders/generic-order-entry';
import { Price } from '../../../models/serializable/price';
import { FeeHelper } from '../../fee-helper';
import { GenericOrderDiscountBreakdownEntry } from '../discount-breakdown';
import { GenericOrderFeeBreakdownEntry } from '../fee-breakdown';
import { IGenericOrderPaymentHelperAdapter } from './generic-order-payment-helper-adapter';

export class ProductEntryPaymentHelperAdapter extends IGenericOrderPaymentHelperAdapter {
  public readonly orderEntryTypeId: string = GenericOrderProductEntry.TYPE_ID;

  static verifyType(dm: IGenericOrderPaymentHelperAdapter): boolean {
    return dm instanceof ProductEntryPaymentHelperAdapter;
  }

  static assertType(dm: IGenericOrderPaymentHelperAdapter): dm is ProductEntryPaymentHelperAdapter {
    return dm instanceof ProductEntryPaymentHelperAdapter;
  }

  getCurrency(order: GenericOrder, orderEntry: GenericOrderEntry): Currency | null {
    if(!GenericOrderProductEntry.assertType(orderEntry)) {
      throw new ArgumentException('order', 'Order entry type is not valid');
    }

    return this._getCurrency(orderEntry);
  }

  calculateDiscountBreakdown(
      order: GenericOrder,
      orderEntry: GenericOrderEntry,
      _orderEntryIndex: number
  ): GenericOrderDiscountBreakdownEntry[] | null {
    if(!GenericOrderProductEntry.assertType(orderEntry)) {
      throw new ArgumentException('order', 'Order entry type is not valid');
    }

    return null;
  }

  calculateFeeBreakdown(
      order: GenericOrder,
      orderEntry: GenericOrderEntry,
      orderEntryIndex: number
  ): GenericOrderFeeBreakdownEntry[] | null {
    if(!GenericOrderProductEntry.assertType(orderEntry)) {
      throw new ArgumentException('order', 'Order entry type is not valid');
    }

    const entries: GenericOrderFeeBreakdownEntry[] = [];

    for(let pi = 0; pi < orderEntry.products.length; pi++) {
      const orderItem = orderEntry.products[pi];
      const result = FeeHelper.calculateFeeValueForProductOrderItem(orderItem, { applyScaling: false });

      if(result != null) {
        entries.push({ ...result, host: { type: 'product', orderEntryIndex, itemIndex: pi } });
      }
    }

    return entries;
  }

  calculateCheckoutPrice(order: GenericOrder, orderEntry: GenericOrderEntry): Price | null {
    if(!GenericOrderProductEntry.assertType(orderEntry)) {
      throw new ArgumentException('order', 'Order entry type is not valid');
    }

    return this.calculatePrice({ orderEntry, applyFee: true });
  }

  calculateIncome(order: GenericOrder, orderEntry: GenericOrderEntry): Price | null {
    if(!GenericOrderProductEntry.assertType(orderEntry)) {
      throw new ArgumentException('order', 'Order entry type is not valid');
    }

    return this.calculatePrice({ orderEntry, applyFee: false });
  }

  calculateIncomeWithoutDiscount(order: GenericOrder, orderEntry: GenericOrderEntry): Price | null {
    if(!GenericOrderProductEntry.assertType(orderEntry)) {
      throw new ArgumentException('order', 'Order entry type is not valid');
    }

    return this.calculatePrice({ orderEntry, applyFee: false });
  }

  calculateDiscount(orderEntry: GenericOrderEntry): Price | null {
    if(!GenericOrderProductEntry.assertType(orderEntry)) {
      throw new ArgumentException('order', 'Order entry type is not valid');
    }

    return null;
  }

  calculateCheckoutPriceForDiscountProvider(orderEntry: GenericOrderEntry): Price | null {
    if(!GenericOrderProductEntry.assertType(orderEntry)) {
      throw new ArgumentException('order', 'Order entry type is not valid');
    }

    return null;
  }

  canCreateRefund(order: GenericOrder, orderEntry: GenericOrderEntry): boolean | null {
    if(!GenericOrderProductEntry.assertType(orderEntry)) {
      throw new ArgumentException('order', 'Order entry type is not valid');
    }

    if(orderEntry.status !== GenericOrderEntryStatus.Refunded) {
      return false;
    }

    return orderEntry.refunds.length <= 0;
  }

  calculateMaxSimulatedRefund(order: GenericOrder, orderEntry: GenericOrderEntry, options: { includeFee: boolean }): Price | null {
    if(!GenericOrderProductEntry.assertType(orderEntry)) {
      throw new ArgumentException('order', 'Order entry type is not valid');
    }

    let orderEntryPrice = this.calculateIncome(order, orderEntry);
    if(orderEntryPrice == null) {
      return null;
    }

    if(orderEntry.applicationFee != null && options.includeFee) {
      orderEntryPrice = orderEntryPrice.add(orderEntry.applicationFee);
    }

    return orderEntryPrice;
  }

  private calculatePrice({ orderEntry, applyFee }: {
    orderEntry: GenericOrderProductEntry,
    applyFee: boolean,
  }): Price | null {
    const currency = this._getCurrency(orderEntry);
    if(currency == null) {
      return null;
    }

    let orderEntryPrice = Price.zero(currency);

    for(const item of orderEntry.products) {
      if(item.product.price.currency != currency) {
        return null;
      }

      orderEntryPrice = orderEntryPrice.add(item.product.price.scale(item.quantity));
    }

    if(applyFee && orderEntry.applicationFee != null) {
      orderEntryPrice = orderEntryPrice.add(orderEntry.applicationFee);
    }

    return orderEntryPrice;
  }

  private _getCurrency(orderEntry: GenericOrderProductEntry): Currency | null {
    if(orderEntry.products.length <= 0) {
      return null;
    }

    return orderEntry.products[0].product.price.currency;
  }
}
